import { ensureOk } from "@/api/apiError";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/api/httpClient";
import type { MessageCreate, Session } from "@/types";

const SESSIONS_QUERY_KEY = ["sessions"];

async function sendMessage(
  sessionId: string,
  payload: MessageCreate,
): Promise<Session> {
  const response = await httpClient(
    `/sessions/${encodeURIComponent(sessionId)}/messages`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
  await ensureOk(response);
  return response.json() as Promise<Session>;
}

/** US-04: synchronous message send (non-streaming fallback) — persists user+assistant
 * messages and returns the updated session; invalidates the sessions list cache. */
export function useSendMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      payload,
    }: {
      sessionId: string;
      payload: MessageCreate;
    }) => sendMessage(sessionId, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: SESSIONS_QUERY_KEY }),
  });
}

export function useUpdateSessionCache() {
  const queryClient = useQueryClient();
  return (session: Session) => {
    queryClient.setQueryData<Session[]>(SESSIONS_QUERY_KEY, (previous) =>
      (previous ?? []).map((item) => (item.id === session.id ? session : item)),
    );
  };
}
